import {
  AgCartesianChartOptions,
  AgChartLegendOptions,
  AgChartOptions,
  AgPolarChartOptions,
  AgSeriesVisibilityChange,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import { SECONDARY_MAX_DECIMAL_DIGITS } from 'business/constants';
import { useSingleGraphReferenceContext } from 'business/providers/graph-reference';
import { useIndicatorAnalyzeFilters } from 'business/shift-report/indicators/pages/indicator-analyze/providers/filters';
import { useReportActivityAnalyzeFilters } from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters';
import {
  ActivityAnalyseLegendFilters,
  useActivityAnalyseLegendFilters,
} from 'business/shift-report/report-analyze/pages/activity-analyze/providers/legend-filters';
import { flattenActivitySeries } from 'business/shift-report/report-analyze/pages/activity-analyze/services/flatten-activity-series';
import {
  formatReportActivityAnalyzeTitleUnit,
  formatReportActivityAnalyzeValueUnit,
} from 'business/shift-report/report-analyze/pages/activity-analyze/services/format-activity-analyze-unit';
import {
  ACTIVITY_DISPLAY_TYPES_MAP,
  ActivityDisplayTypes,
  ReportActivityAnalyzeGraphPoint,
  ReportActivitySerie,
} from 'business/shift-report/report-analyze/pages/activity-analyze/types';
import { useChartResize } from 'technical/chart-resize';
import { disableLegendDoubleClick } from 'technical/graph/disable-legend-double-clidk';
import { hasInitialZoom } from 'technical/graph/has-initial-zoom';
import { useMediaType } from 'technical/media/hooks';
import { toPercent } from 'technical/utils/converter';
import { NumberRangeValue } from 'ui/form/number-range-input';

type ReportActivityAnalyzeGraphProps = {
  graphType: Extract<ActivityDisplayTypes, 'bar' | 'pie'>;
  reportActivityAnalyzeGraphData: ReportActivityAnalyzeGraphPoint[];
  reportActivitySeries: ReportActivitySerie[];
};

const getCommonLegendOptions = (isMobile: boolean) => {
  return {
    enabled: true,
    listeners: {
      legendItemDoubleClick: disableLegendDoubleClick,
    },
    maxHeight: isMobile ? 60 : undefined,
    maxWidth: isMobile ? 500 : undefined,
  } satisfies AgChartLegendOptions;
};

const formatLegend = (datum: any, totalMinutes: number, locale: string) => {
  return `${datum.legendActivity} - ${toPercent(datum.value / (totalMinutes === 0 ? 1 : totalMinutes)).toLocaleString(locale, { maximumFractionDigits: SECONDARY_MAX_DECIMAL_DIGITS })}%`;
};

const getBarChartOptions = (
  reportActivitySeries: ReportActivitySerie[],
  yAxisScale: NumberRangeValue,
  isMobile: boolean,
) => {
  return {
    series: reportActivitySeries.map((serie) => ({
      type: 'bar',
      xKey: 'rowDisplay',
      yKey: serie.id,
      yName: serie.nameLevelMax,
      legendItemName: formatReportActivityAnalyzeTitleUnit(serie.nameLevelMax),
      strokeWidth: 1,
      tooltip: {
        renderer: (params) => ({
          data: [
            {
              // Assert here because it is defined in the serie definition
              label: params.yName!,
              value: formatReportActivityAnalyzeValueUnit(
                params.datum[params.yKey],
              ),
            },
          ],
        }),
      },
    })),
    axes: [
      {
        type: 'category',
        position: 'bottom',
      },
      {
        type: 'number',
        position: 'left',
        nice: false,
        min: yAxisScale?.[0] ?? 0,
        max: yAxisScale?.[1] ?? undefined,
      },
    ],
    legend: {
      ...getCommonLegendOptions(isMobile),
    },
  } satisfies AgCartesianChartOptions;
};

const getPieChartOptions = (
  data: ReportActivityAnalyzeGraphPoint[],
  locale: string,
) => {
  const totalMinutes = data.reduce((sum, item) => {
    return sum + (typeof item.value === 'number' ? item.value : 0);
  }, 0);

  const getPercentage = (value: number) =>
    toPercent(value / totalMinutes).toLocaleString(locale, {
      maximumFractionDigits: SECONDARY_MAX_DECIMAL_DIGITS,
    });

  return {
    series: [
      {
        type: 'pie',
        angleKey: 'value',
        calloutLabelKey: 'activity',
        legendItemKey: 'legendActivity',
        sectorLabel: {
          formatter: ({ value }) => {
            const percentage = getPercentage(value);
            return `${formatReportActivityAnalyzeValueUnit(value)} (${percentage}%)`;
          },
        },
        tooltip: {
          renderer: ({ datum: { activity, value } }) => {
            const percentage = getPercentage(value);
            return {
              data: [
                {
                  label: activity,
                  value: `${formatReportActivityAnalyzeValueUnit(value)} (${percentage}%)`,
                },
              ],
            };
          },
        },
      },
    ],
    legend: {
      enabled: true,
      listeners: {
        legendItemDoubleClick: disableLegendDoubleClick,
      },
      item: {
        label: {
          formatter: ({ datum }) => {
            return formatLegend(datum, totalMinutes, locale);
          },
        },
      },
    },
  } satisfies AgPolarChartOptions;
};

const formatBarChartData = (data: ReportActivityAnalyzeGraphPoint[]) => data;

const formatPieChartData = (
  data: ReportActivityAnalyzeGraphPoint[],
  series: ReportActivitySerie[],
) => {
  const filteredData = data.filter((item) => {
    return Object.keys(item).length > 1;
  });

  if (filteredData.length !== 1) {
    return [];
  }

  const seriesMap = flattenActivitySeries(series);
  const [pieChartData] = filteredData;

  return Object.entries(pieChartData).map(([id, value]) => {
    return {
      activity: seriesMap[id]?.nameLevelMax,
      legendActivity: formatReportActivityAnalyzeTitleUnit(
        seriesMap[id]?.nameLevelMax,
      ),
      value,
    };
  });
};

const DISPLAY_TYPE_TO_DATA_FORMATER = {
  [ACTIVITY_DISPLAY_TYPES_MAP.bar]: formatBarChartData,
  [ACTIVITY_DISPLAY_TYPES_MAP.pie]: formatPieChartData,
};

const DISPLAY_TYPE_TO_GRAPH_OPTIONS_GETTER = {
  [ACTIVITY_DISPLAY_TYPES_MAP.bar]: getBarChartOptions,
  [ACTIVITY_DISPLAY_TYPES_MAP.pie]: getPieChartOptions,
};

const updateSelectedActivities = (
  currentActivityId: string | undefined,
  activityIds: string[],
  selectedActivityIds: string[],
  setSelectedIndicators: (
    value: SetStateAction<ActivityAnalyseLegendFilters>,
  ) => void,
  visible: boolean,
) => {
  const newLegendFilters = new Set(selectedActivityIds);
  if (currentActivityId) {
    if (!visible && newLegendFilters.has(currentActivityId)) {
      newLegendFilters.delete(currentActivityId);
    } else if (visible) {
      newLegendFilters.add(currentActivityId);
    }
  }

  setSelectedIndicators({
    allActivities: newLegendFilters.size === activityIds.length,
    selectedActivities: Array.from(newLegendFilters),
  });
};

const getActivityId = (
  itemId: string | number | undefined,
  legendItemName: string | undefined,
  activities: ReportActivitySerie[],
) => {
  if (typeof itemId === 'undefined') {
    return undefined;
  }

  if (typeof itemId === 'string') {
    return itemId;
  }

  return activities.find(
    (activity) =>
      activity.nameLevelMax === legendItemName?.replace(' (min)', ''),
  )?.id;
};

const updateVisibility =
  (
    activities: ReportActivitySerie[],
    legendFilters: ActivityAnalyseLegendFilters,
    setLegendFilters: Dispatch<SetStateAction<ActivityAnalyseLegendFilters>>,
  ) =>
  ({ itemId, legendItemName, visible }: AgSeriesVisibilityChange) => {
    if (itemId) {
      const currentActivityId = getActivityId(
        itemId,
        legendItemName,
        activities,
      );
      const activityIds = activities.map((activity) => activity.id);
      if (legendFilters.allActivities) {
        updateSelectedActivities(
          currentActivityId,
          activityIds,
          activityIds,
          setLegendFilters,
          visible,
        );
        return;
      }

      updateSelectedActivities(
        currentActivityId,
        activityIds,
        legendFilters.selectedActivities,
        setLegendFilters,
        visible,
      );
    }
  };

const useResetLegendFiltersEffect = (
  reportActivitySeries: ReportActivitySerie[],
  legendFilters: ActivityAnalyseLegendFilters,
  setLegendFilters: Dispatch<SetStateAction<ActivityAnalyseLegendFilters>>,
) => {
  useEffect(() => {
    if (!reportActivitySeries?.length) {
      return;
    }

    const activityIds = reportActivitySeries.map((activity) => activity.id);
    const newLegendFilters = new Set(activityIds);

    const hasChanged =
      legendFilters.selectedActivities.length !== newLegendFilters.size;

    if (hasChanged) {
      setLegendFilters({
        allActivities: newLegendFilters.size === activityIds.length,
        selectedActivities: Array.from(newLegendFilters),
      });
    }
    // Reset legend visibility when the selected activities are updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportActivitySeries]);
};

const ReportActivityAnalyzeGraph = ({
  graphType,
  reportActivityAnalyzeGraphData,
  reportActivitySeries,
}: ReportActivityAnalyzeGraphProps) => {
  const { language } = useTranslation();
  const { ref: chartRef } = useSingleGraphReferenceContext();
  const { vChartScale } = useReportActivityAnalyzeFilters();

  const { legendFilters, setLegendFilters } = useActivityAnalyseLegendFilters();
  useResetLegendFiltersEffect(
    reportActivitySeries,
    legendFilters,
    setLegendFilters,
  );

  const data = DISPLAY_TYPE_TO_DATA_FORMATER[graphType](
    reportActivityAnalyzeGraphData,
    reportActivitySeries,
  );

  const getSeriesAndAxisOptions =
    graphType === ACTIVITY_DISPLAY_TYPES_MAP.pie
      ? () => DISPLAY_TYPE_TO_GRAPH_OPTIONS_GETTER[graphType](data, language)
      : DISPLAY_TYPE_TO_GRAPH_OPTIONS_GETTER[graphType];
  const filters = useIndicatorAnalyzeFilters();

  const { isMobile } = useMediaType();
  const initialZoom = hasInitialZoom(isMobile, filters);

  const chartOptions = {
    data,
    ...getSeriesAndAxisOptions(reportActivitySeries, vChartScale, isMobile),
    listeners: {
      seriesVisibilityChange: updateVisibility(
        reportActivitySeries,
        legendFilters,
        setLegendFilters,
      ),
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          initialState: {
            zoom: initialZoom
              ? {
                  ratioX: {
                    start: 0,
                    end: 0.4,
                  },
                }
              : {},
          },
          contextMenu: {
            enabled: false,
          },
        }
      : {
          zoom: {
            // zoom on where the mouse is
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
          },
        }),
  } satisfies AgChartOptions;

  useChartResize(chartRef);

  return <AgCharts options={chartOptions} ref={chartRef} />;
};

export default ReportActivityAnalyzeGraph;
